/** @jsxImportSource @emotion/react */
import { useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  List,
  ListItem,
  Snackbar,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useAsyncFn } from 'react-use';
import { Divider } from 'components';
import { Permissions } from '../../constants';
import { getBackofficeUsers } from './api';
import { AddBackofficeUserModal } from '.';

interface Props {
  merchantId: string;
  onSuccess: (message: string) => void;
  onError?: (message: string) => void;
}
export const BackofficeUsers = ({ merchantId, onSuccess }: Props) => {
  const { spacing, sizing } = useTheme();
  const session = useSession();
  const [showAddBOUserModal, setShowAddBOUserModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [{ value, loading }, getUsers] = useAsyncFn(
    () => getBackofficeUsers(merchantId),
    [merchantId]
  );

  const disableAddAndUserList = !(
    session.authenticated &&
    session.permissions.includes(Permissions.ADD_MERCHANT_BACKOFFICE_USER)
  );

  return (
    <>
      <Card
        css={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle">Backoffice Users</Typography>
          {!disableAddAndUserList && (
            <UtilityButton
              text="Add user"
              variant="primary"
              icon="plus"
              onClick={() => setShowAddBOUserModal(true)}
            />
          )}
        </div>
        <Divider />
        {disableAddAndUserList && (
          <div
            css={{
              padding: spacing.xxs,
              marginTop: spacing.xxs,
              textAlign: 'center',
            }}
          >
            <Typography variant="bodyBold">
              {`You do not have permission to add or view backoffice
              users`}
            </Typography>
          </div>
        )}
        {!disableAddAndUserList && value === undefined && (
          <div
            css={{
              minHeight: spacing.xl,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UtilityButton
              variant="primary"
              onClick={getUsers}
              text="Show users"
              loading={loading}
              disabled={loading}
            />
          </div>
        )}
        {value && value.length > 0 && (
          <div
            css={{
              marginTop: spacing.xs,
              maxHeight: sizing.small,
              overflow: 'scroll',
            }}
          >
            <List>
              {value.map((user) => (
                <ListItem
                  key={user.userId}
                  title={user.email}
                  subtitle={
                    <div
                      css={{
                        display: 'flex',
                        gap: `calc(${spacing.xxxs}/2)`,
                      }}
                    >
                      <div>
                        <Typography variant="bodySmallestBold">
                          {user.createdBy && (
                            <>
                              Created by:
                              <br />
                            </>
                          )}
                          {user.deletedAt && <>Deleted at:</>}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="bodySmallest">
                          {user.createdBy && (
                            <>
                              {user.createdBy}
                              <br />
                            </>
                          )}
                          {user.deletedAt && (
                            <>
                              {user.deletedAt.toLocaleString()}
                              <br />
                            </>
                          )}
                        </Typography>
                      </div>
                    </div>
                  }
                />
              ))}
            </List>
          </div>
        )}
        {value && value.length === 0 && (
          <div
            css={{
              padding: spacing.xxs,
              marginTop: spacing.xxs,
              textAlign: 'center',
            }}
          >
            <Typography variant="bodyBold">No users</Typography>
          </div>
        )}
      </Card>
      {merchantId && showAddBOUserModal && (
        <AddBackofficeUserModal
          merchantId={merchantId}
          onClose={async (added: boolean) => {
            setShowAddBOUserModal(false);
            if (added) {
              await getUsers();
              onSuccess('User successfully added.');
            }
          }}
        />
      )}
      <Snackbar
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        variant="danger"
      />
    </>
  );
};
